import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AddCategoryForm,
  CategoryCard,
  CategoryModal,
  ChallengeFormModal,
  formatPeriod,
  PhaseBadge,
} from '@/components/challenges'
import { MemberAvatar } from '@/components/clubs'
import { Button, Card, CardContent, ConfirmDialog, Select } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import {
  useChallenge,
  useChallengeBoard,
  useDeleteChallenge,
  useUpdateChallenge,
} from '@/hooks/challenges'
import { useClub, useClubMembers } from '@/hooks/clubs'
import { useLibraryBooks } from '@/hooks/insights'
import { challengePhase, challengeTotal, memberProgress, suggestionQuota } from '@/lib/challenges'

type Filter = 'all' | 'todo' | 'picked' | 'done'

export function ChallengePage() {
  const { t, i18n } = useTranslation()
  const { clubId, challengeId } = useParams<{ clubId: string; challengeId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: club } = useClub(clubId)
  const { data: challenge, isLoading } = useChallenge(challengeId)
  const { data: board } = useChallengeBoard(challengeId)
  const { data: members = [] } = useClubMembers(clubId)
  const { data: myBooks = [] } = useLibraryBooks()
  const updateChallenge = useUpdateChallenge()
  const deleteChallenge = useDeleteChallenge()
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')

  const memberMap = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members])
  const categories = board?.categories ?? []
  const entries = board?.entries ?? []

  const progress = useMemo(
    () =>
      memberProgress(
        members.map((m) => m.user_id),
        categories,
        entries
      ),
    [members, categories, entries]
  )

  if (isLoading) return <div className="h-40 animate-pulse rounded-lg bg-muted" />

  if (!challenge || !clubId) {
    return (
      <div className="py-12 text-center">
        <p className="mb-4 text-muted-foreground">{t('challenges.notFound')}</p>
        <Link to="/dashboard/clubs" className="text-primary hover:underline">
          {t('clubs.backToClubs')}
        </Link>
      </div>
    )
  }

  const canManage = challenge.created_by === user?.id || club?.my_role === 'admin'
  const total = challengeTotal(challenge, categories.length)
  const quota = suggestionQuota(challenge, members.length)
  const mySuggestions = categories.filter((c) => c.suggested_by === user?.id).length
  const myEntryFor = (categoryId: string) =>
    entries.find((e) => e.category_id === categoryId && e.user_id === user?.id)

  const visible = categories
    .map((category, index) => ({ category, index }))
    .filter(({ category }) => {
      const mine = myEntryFor(category.id)
      if (filter === 'todo') return !mine
      if (filter === 'picked') return !!mine && !mine.completed_on
      if (filter === 'done') return !!mine?.completed_on
      return true
    })

  const openIndex = categories.findIndex((c) => c.id === openCategoryId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/dashboard/clubs/${clubId}`}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {club?.name ?? t('clubs.backToClubs')}
        </Link>
        <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="break-words text-3xl font-bold text-foreground">{challenge.name}</h1>
              <PhaseBadge phase={challengePhase(challenge)} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatPeriod(challenge, i18n.language)} ·{' '}
              {t('challenges.categoryProgress', { count: categories.length, total })}
            </p>
            {challenge.description && (
              <p className="mt-2 whitespace-pre-wrap break-words text-muted-foreground">
                {challenge.description}
              </p>
            )}
          </div>
          {canManage && (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                {t('common.edit')}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(true)}>
                {t('common.delete')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Members' progress */}
      <Card className="border-0 shadow-sm">
        <CardContent className="space-y-3 p-5">
          {progress.map((p) => {
            const member = memberMap.get(p.userId)
            if (!member) return null
            const percent = total ? Math.round((p.completed / total) * 100) : 0
            const pickedPercent = total ? Math.round((p.indicated / total) * 100) : 0
            return (
              <div key={p.userId} className="flex items-center gap-3">
                <MemberAvatar
                  userId={member.user_id}
                  name={member.display_name}
                  avatarUrl={member.avatar_url}
                  size="sm"
                />
                <span className="w-24 shrink-0 truncate text-sm font-medium text-foreground sm:w-32">
                  {p.userId === user?.id ? t('clubs.members.you') : member.display_name}
                </span>
                <div className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-success" style={{ width: `${percent}%` }} />
                  <div className="h-full bg-warning/60" style={{ width: `${pickedPercent}%` }} />
                </div>
                <span className="w-14 shrink-0 text-right text-sm font-semibold text-foreground">
                  {p.completed}/{total}
                </span>
              </div>
            )
          })}
          <p className="text-xs text-muted-foreground">
            <span className="mr-1 inline-block h-2 w-2 rounded-full bg-success" />
            {t('challenges.legend.done')}
            <span className="ml-3 mr-1 inline-block h-2 w-2 rounded-full bg-warning/60" />
            {t('challenges.legend.picked')}
          </p>
        </CardContent>
      </Card>

      {/* Categories */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {t('challenges.categories.title')}
          </h2>
          <Select
            aria-label={t('common.filter')}
            size="sm"
            value={filter}
            onChange={setFilter}
            className="sm:w-56"
            options={[
              { value: 'all', label: t('challenges.filter.all') },
              { value: 'todo', label: t('challenges.filter.todo') },
              { value: 'picked', label: t('challenges.filter.picked') },
              { value: 'done', label: t('challenges.filter.done') },
            ]}
          />
        </div>

        <AddCategoryForm
          challengeId={challenge.id}
          categories={categories}
          mySuggestions={mySuggestions}
          quota={quota}
        />

        {categories.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('challenges.categories.empty')}
          </p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('common.noResultsMessage')}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map(({ category, index }) => {
              const categoryEntries = entries.filter((e) => e.category_id === category.id)
              return (
                <CategoryCard
                  key={category.id}
                  category={category}
                  index={index}
                  entries={categoryEntries}
                  members={members}
                  myUserId={user?.id}
                  commentTotal={categoryEntries.reduce(
                    (sum, e) => sum + (board?.commentCounts[e.id] ?? 0),
                    0
                  )}
                  onOpen={() => setOpenCategoryId(category.id)}
                />
              )
            })}
          </div>
        )}
      </div>

      {board && openIndex >= 0 && (
        <CategoryModal
          challenge={challenge}
          category={categories[openIndex]}
          index={openIndex}
          board={board}
          members={memberMap}
          canManage={canManage}
          myBooks={myBooks}
          onClose={() => setOpenCategoryId(null)}
        />
      )}

      {editing && (
        <ChallengeFormModal
          title={t('challenges.edit')}
          initial={challenge}
          isPending={updateChallenge.isPending}
          error={updateChallenge.isError}
          onClose={() => setEditing(false)}
          onSubmit={async (values) => {
            await updateChallenge.mutateAsync({ id: challenge.id, ...values })
            setEditing(false)
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title={t('challenges.deleteTitle')}
        message={t('challenges.deleteMessage', { name: challenge.name })}
        confirmText={t('common.delete')}
        variant="danger"
        isLoading={deleteChallenge.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await deleteChallenge.mutateAsync(challenge)
          navigate(`/dashboard/clubs/${clubId}`)
        }}
      />
    </div>
  )
}
